import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

// Google Maps Platform via Lovable connector gateway.
// - Places API (New) searchText for autocomplete / geocoding
// - Routes API computeRoutes for driving distance + encoded polyline
// Falls back to a Haversine estimate if the upstream is unreachable.

const FETCH_TIMEOUT_MS = 10_000;
const GATEWAY_URL = "https://connector-gateway.lovable.dev/google_maps";

function connectorKeys() {
  const lovable = process.env.LOVABLE_API_KEY;
  const google = process.env.GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_SERVER_KEY;
  if (!lovable || !google) throw new Error("Maps service not configured.");
  return { lovable, google };
}

function gatewayHeaders(extra: Record<string, string> = {}) {
  const { lovable, google } = connectorKeys();
  return {
    Authorization: `Bearer ${lovable}`,
    "X-Connection-Api-Key": google,
    "Content-Type": "application/json",
    ...extra,
  };
}

async function fetchWithTimeout(url: string, init: RequestInit = {}) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}


const SearchSchema = z.object({
  q: z.string().trim().min(1).max(120),
});

export interface PlaceSuggestion {
  label: string;
  lat: number;
  lng: number;
}

export const searchPlaces = createServerFn({ method: "POST" })
  .inputValidator((d) => SearchSchema.parse(d))
  .handler(async ({ data }): Promise<PlaceSuggestion[]> => {
    try {
      const res = await fetchWithTimeout(`${GATEWAY_URL}/places/v1/places:searchText`, {
        method: "POST",
        headers: gatewayHeaders({
          "X-Goog-FieldMask":
            "places.displayName,places.formattedAddress,places.location",
        }),
        body: JSON.stringify({
          textQuery: data.q,
          regionCode: "MA",
          languageCode: "en",
          maxResultCount: 6,
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error("[searchPlaces] upstream", res.status, body.slice(0, 500));
        return [];
      }
      const json = (await res.json()) as {
        places?: Array<{
          displayName?: { text?: string };
          formattedAddress?: string;
          location?: { latitude: number; longitude: number };
        }>;
      };
      const out = (json.places ?? [])
        .filter((p) => p.location)
        .map((p) => ({
          label: [p.displayName?.text, p.formattedAddress].filter(Boolean).join(" — ") || (p.formattedAddress ?? ""),
          lat: p.location!.latitude,
          lng: p.location!.longitude,
        }));
      console.log("[searchPlaces]", data.q, "→", out.length);
      return out;
    } catch (e) {
      console.error("[searchPlaces] threw", (e as Error).message);
      return [];
    }
  });

const ReverseSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});

export const reverseGeocode = createServerFn({ method: "POST" })
  .inputValidator((d) => ReverseSchema.parse(d))
  .handler(async ({ data }): Promise<PlaceSuggestion | null> => {
    try {
      const url = `${GATEWAY_URL}/maps/api/geocode/json?latlng=${data.lat},${data.lng}&language=en`;
      const res = await fetchWithTimeout(url, { headers: gatewayHeaders() });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        console.error("[reverseGeocode] upstream", res.status, body.slice(0, 300));
        return { label: `Pinned location (${data.lat.toFixed(4)}, ${data.lng.toFixed(4)})`, lat: data.lat, lng: data.lng };
      }
      const json = (await res.json()) as {
        status?: string;
        results?: Array<{ formatted_address?: string }>;
      };
      const first = json.results?.[0];
      if (!first?.formatted_address) {
        return { label: `Pinned location (${data.lat.toFixed(4)}, ${data.lng.toFixed(4)})`, lat: data.lat, lng: data.lng };
      }
      return { label: first.formatted_address, lat: data.lat, lng: data.lng };
    } catch {
      return { label: `Pinned location (${data.lat.toFixed(4)}, ${data.lng.toFixed(4)})`, lat: data.lat, lng: data.lng };
    }
  });



const RouteSchema = z.object({
  pickupLat: z.number(),
  pickupLng: z.number(),
  dropoffLat: z.number(),
  dropoffLng: z.number(),
});

export interface RouteResult {
  distanceKm: number;
  durationMin: number;
  estimated: boolean;
  polyline?: string; // encoded polyline (Google format)
}

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export const computeRoute = createServerFn({ method: "POST" })
  .inputValidator((d) => RouteSchema.parse(d))
  .handler(async ({ data }): Promise<RouteResult> => {
    try {
      const res = await fetchWithTimeout(`${GATEWAY_URL}/routes/directions/v2:computeRoutes`, {
        method: "POST",
        headers: gatewayHeaders({
          "X-Goog-FieldMask":
            "routes.distanceMeters,routes.duration,routes.polyline.encodedPolyline",
        }),
        body: JSON.stringify({
          origin: { location: { latLng: { latitude: data.pickupLat, longitude: data.pickupLng } } },
          destination: { location: { latLng: { latitude: data.dropoffLat, longitude: data.dropoffLng } } },
          travelMode: "DRIVE",
          routingPreference: "TRAFFIC_AWARE",
          polylineEncoding: "ENCODED_POLYLINE",
        }),
      });
      if (res.ok) {
        const json = (await res.json()) as {
          routes?: Array<{
            distanceMeters?: number;
            duration?: string; // e.g. "1234s"
            polyline?: { encodedPolyline?: string };
          }>;
        };
        const r = json.routes?.[0];
        if (r?.distanceMeters && r.duration) {
          const seconds = parseInt(r.duration.replace("s", ""), 10);
          return {
            distanceKm: Math.round((r.distanceMeters / 1000) * 10) / 10,
            durationMin: Math.round(seconds / 60),
            estimated: false,
            polyline: r.polyline?.encodedPolyline,
          };
        }
      }
    } catch {
      // fall through to estimate
    }

    const straight = haversineKm(data.pickupLat, data.pickupLng, data.dropoffLat, data.dropoffLng);
    const km = isFinite(straight) && straight > 0 ? straight * 1.3 : 0;
    return {
      distanceKm: Math.round(km * 10) / 10,
      durationMin: Math.round((km / 70) * 60),
      estimated: true,
    };
  });
